import * as React from "react";
import {
  currentMonthPeriod,
  describePeriod,
  EMPTY_USAGE_PERIOD,
  formatCost,
  formatCount,
  formatDateTime,
  formatDay,
  lastDaysPeriod,
  usageKindLabel,
} from "@/lib/usage";
import type {
  UsageDailyPoint,
  UsageKindBreakdownItem,
  UsagePeriodState,
  UsageTotals,
} from "@/lib/usage";

// ---------------------------------------------------------------------------
// Period filter
// ---------------------------------------------------------------------------

interface UsagePeriodBarProps {
  value: UsagePeriodState;
  onChange: (period: UsagePeriodState) => void;
  /** Rendered inline with the presets — used for the "обновить" affordance. */
  trailing?: React.ReactNode;
}

const PRESETS: { label: string; build: () => UsagePeriodState }[] = [
  { label: "7 дней", build: () => lastDaysPeriod(7) },
  { label: "30 дней", build: () => lastDaysPeriod(30) },
  { label: "Текущий месяц", build: () => currentMonthPeriod() },
  { label: "Всё время", build: () => EMPTY_USAGE_PERIOD },
];

export function UsagePeriodBar({
  value,
  onChange,
  trailing,
}: UsagePeriodBarProps): React.JSX.Element {
  return (
    <div
      role="search"
      aria-label="Период расходов"
      className="flex items-center gap-3 flex-wrap mb-5"
    >
      <div className="flex items-center gap-1.5">
        <label htmlFor="usage-from" className="text-[13px] text-[var(--muted)] whitespace-nowrap">
          С
        </label>
        <input
          id="usage-from"
          type="date"
          value={value.from}
          max={value.to || undefined}
          onChange={(e) => onChange({ ...value, from: e.target.value })}
          className="h-9 px-3 border border-[var(--border)] rounded-[var(--radius-sm)] text-[13px] text-[var(--fg)] bg-[var(--surface-2)] outline-none focus:border-[var(--accent)]"
        />
      </div>
      <div className="flex items-center gap-1.5">
        <label htmlFor="usage-to" className="text-[13px] text-[var(--muted)] whitespace-nowrap">
          По
        </label>
        <input
          id="usage-to"
          type="date"
          value={value.to}
          min={value.from || undefined}
          onChange={(e) => onChange({ ...value, to: e.target.value })}
          className="h-9 px-3 border border-[var(--border)] rounded-[var(--radius-sm)] text-[13px] text-[var(--fg)] bg-[var(--surface-2)] outline-none focus:border-[var(--accent)]"
        />
      </div>

      <div className="flex items-center gap-1.5">
        {PRESETS.map((preset) => {
          const candidate = preset.build();
          const active = candidate.from === value.from && candidate.to === value.to;
          return (
            <button
              key={preset.label}
              type="button"
              aria-pressed={active}
              onClick={() => onChange(candidate)}
              className={[
                "h-9 px-3 rounded-[var(--radius-sm)] text-[13px] font-[500] border transition-colors",
                active
                  ? "border-[var(--accent)] text-[var(--accent)] bg-[var(--accent-weak)]"
                  : "border-[var(--border)] text-[var(--muted)] bg-[var(--surface-2)] hover:text-[var(--fg)]",
              ].join(" ")}
            >
              {preset.label}
            </button>
          );
        })}
      </div>

      {trailing}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stat cards
// ---------------------------------------------------------------------------

interface StatCardProps {
  label: string;
  value: string;
  hint?: string;
  accent?: boolean;
}

export function StatCard({ label, value, hint, accent }: StatCardProps): React.JSX.Element {
  return (
    <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[var(--radius)] p-4">
      <div className="text-[11px] font-[600] uppercase tracking-wide text-[var(--muted)] mb-1.5">
        {label}
      </div>
      <div
        className={[
          "text-[22px] font-[700] tracking-tight tabular-nums",
          accent ? "text-[var(--accent)]" : "text-[var(--fg)]",
        ].join(" ")}
      >
        {value}
      </div>
      {hint && <div className="text-[12px] text-[var(--muted)] mt-1">{hint}</div>}
    </div>
  );
}

interface UsageTotalsCardsProps {
  period: UsageTotals;
  allTime: UsageTotals;
  periodState: UsagePeriodState;
  /** Console-wide view shows the distinct-accounts figure; a user card does not. */
  showAccounts?: boolean;
}

export function UsageTotalsCards({
  period,
  allTime,
  periodState,
  showAccounts = false,
}: UsageTotalsCardsProps): React.JSX.Element {
  return (
    <div
      className={[
        "grid gap-3 mb-5",
        showAccounts ? "grid-cols-2 md:grid-cols-4" : "grid-cols-2 md:grid-cols-3",
      ].join(" ")}
    >
      <StatCard
        label="Расходы за период"
        value={formatCost(period.totalCost)}
        hint={describePeriod(periodState)}
        accent
      />
      <StatCard
        label="Расходы за всё время"
        value={formatCost(allTime.totalCost)}
        hint={
          allTime.lastActivityAt
            ? `последняя операция ${formatDateTime(allTime.lastActivityAt)}`
            : "операций не было"
        }
      />
      <StatCard
        label="Операции за период"
        value={formatCount(period.operations)}
        hint={
          period.failedOperations > 0
            ? `неуспешных: ${formatCount(period.failedOperations)}`
            : `токенов: ${formatCount(period.totalTokens)}`
        }
      />
      {showAccounts && (
        <StatCard
          label="Активных аккаунтов"
          value={formatCount(period.accounts)}
          hint={`всего за период · из ${formatCount(allTime.accounts)} за всё время`}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Breakdown by kind
// ---------------------------------------------------------------------------

export function UsageKindBreakdown({
  items,
}: {
  items: UsageKindBreakdownItem[];
}): React.JSX.Element {
  const total = items.reduce((sum, item) => sum + item.totalCost, 0);

  return (
    <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[var(--radius)] p-5">
      <h3 className="text-[14px] font-[600] mb-3.5">По типу операций</h3>
      {items.length === 0 ? (
        <p className="text-[13px] text-[var(--muted)]">За выбранный период операций не было.</p>
      ) : (
        <ul role="list" className="flex flex-col gap-3">
          {items.map((item) => {
            const share = total > 0 ? (item.totalCost / total) * 100 : 0;
            return (
              <li key={item.kind}>
                <div className="flex items-baseline justify-between gap-3 mb-1">
                  <span className="text-[13px] font-[500]">{usageKindLabel(item.kind)}</span>
                  <span className="text-[13px] tabular-nums">
                    {formatCost(item.totalCost)}
                    <span className="text-[var(--muted)] text-[12px] ml-2">
                      {formatCount(item.operations)} оп. · {formatCount(item.totalTokens)} ток.
                    </span>
                  </span>
                </div>
                <div
                  className="h-1.5 rounded-full bg-[var(--surface-2)] overflow-hidden"
                  role="img"
                  aria-label={`${usageKindLabel(item.kind)}: ${share.toFixed(0)}% расходов`}
                >
                  <div
                    className="h-full bg-[var(--accent)]"
                    style={{ width: `${Math.max(share, 1)}%` }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Daily series
// ---------------------------------------------------------------------------

export function UsageDailyChart({ points }: { points: UsageDailyPoint[] }): React.JSX.Element {
  // Backend returns newest-first; the chart reads left-to-right chronologically.
  const ordered = [...points].reverse();
  const max = ordered.reduce((peak, point) => Math.max(peak, point.totalCost), 0);

  return (
    <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[var(--radius)] p-5">
      <h3 className="text-[14px] font-[600] mb-3.5">По дням</h3>
      {ordered.length === 0 ? (
        <p className="text-[13px] text-[var(--muted)]">За выбранный период операций не было.</p>
      ) : (
        <div className="flex items-end gap-1 h-[120px]" role="list" aria-label="Расходы по дням">
          {ordered.map((point) => {
            const height = max > 0 ? Math.max((point.totalCost / max) * 100, 2) : 2;
            return (
              <div
                key={point.day}
                role="listitem"
                title={`${formatDay(point.day)}: ${formatCost(point.totalCost)} · ${formatCount(point.operations)} оп.`}
                aria-label={`${formatDay(point.day)}: ${formatCost(point.totalCost)}`}
                className="flex-1 min-w-[4px] bg-[var(--accent)] rounded-t-[2px] opacity-80 hover:opacity-100"
                style={{ height: `${height}%` }}
              />
            );
          })}
        </div>
      )}
      {ordered.length > 0 && (
        <div className="flex justify-between text-[11px] text-[var(--muted)] mt-2">
          <span>{formatDay(ordered[0]!.day)}</span>
          <span>{formatDay(ordered[ordered.length - 1]!.day)}</span>
        </div>
      )}
    </div>
  );
}
